import { ResponseStatusEnum } from "../../../helpers/GlobalEnum";
import { alertedProductsServices } from "../../../helpers/services/AlertedProductsServices";
import { nextRowKey } from "../../../shared/lib/rowKey";
import { getAlertedProductsSolicitudDetalle } from "./alertedProductsSolicitudesApi";

// Nombres de archivo embebidos en el texto (ej. "1_20260630194204_test.pdf").
const FILENAME_RE = /([^\s:/\\()]+\.[A-Za-z0-9]{2,5})/g;

const extractFilenames = (observacion = "") =>
  (String(observacion).match(FILENAME_RE) || []).map((name) => name.trim());

const findDocumento = (filename, documentos = []) =>
  documentos.find((doc) => {
    const nombre = String(doc?.nombre ?? "").trim();
    return (
      nombre &&
      (filename === nombre || filename.includes(nombre) || nombre.includes(filename))
    );
  });

// Cada evento referencia sus soportes por el nombre de archivo embebido en la
// observación (ej. "Documento Cargado: 1_..._test.pdf()"). Se extraen todos los
// nombres del texto y, por cada uno, se busca su documento en el detalle para
// habilitar Ver/Descargar. Si el archivo ya no está en el detalle (p. ej. fue
// reemplazado en una subsanación), se muestra el nombre sin acciones. Los eventos
// sin nombre de archivo en el texto no tienen soportes ("---").
const buildSoportes = (observacion = "", documentos = []) =>
  extractFilenames(observacion).map((filename) => {
    const doc = findDocumento(filename, documentos);
    if (doc) return doc;
    return {
      nombre: filename,
      rutaArchivo: "",
      esPdf: filename.toLowerCase().endsWith(".pdf"),
    };
  });

const normalizeTrazaEvento = (item = {}, documentos = []) => ({
  rowKey: nextRowKey(),
  // "Evento" aún no lo entrega el backend.
  evento: item?.evento ?? "",
  observacion: item?.observacion ?? "",
  // El usuario del evento es el correo que lo registró.
  usuario: item?.correo ?? "",
  // "Rol" aún no lo entrega el backend.
  rol: item?.rol ?? "",
  fecha: item?.fCrea ?? item?.fecha ?? "",
  tipo: item?.tipo ?? "",
  // "Estado" aún no lo entrega el backend.
  estado: item?.estado ?? "",
  soportes: buildSoportes(item?.observacion, documentos),
});

// Ordena los eventos de la fecha más reciente a la menos reciente.
const sortByFechaDesc = (a, b) =>
  new Date(b?.fecha || 0).getTime() - new Date(a?.fecha || 0).getTime();

export const getAlertedProductsTraza = async (idEncabezado) => {
  // Se consumen ambos recursos: traza/ (eventos) y solicitud/detalle/ (documentos
  // para resolver los soportes documentales por nombre).
  const [trazaResponse, detalle] = await Promise.all([
    alertedProductsServices.getTraza(idEncabezado),
    getAlertedProductsSolicitudDetalle(idEncabezado).catch(() => ({ documentos: [] })),
  ]);

  if (trazaResponse?.status !== ResponseStatusEnum.OK) {
    throw trazaResponse;
  }

  const data = trazaResponse?.data ?? [];
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.eventos)
    ? data.eventos
    : [];
  const documentos = Array.isArray(detalle?.documentos) ? detalle.documentos : [];

  // Resumen de la alerta: todos los productos asociados y el estado de la
  // solicitud desde el detalle. (El "Tipo de gestión" lo aporta la fila de la
  // tabla, no el detalle.)
  const productosAsociados = Array.isArray(detalle?.productosAsociados)
    ? detalle.productosAsociados
    : [];
  const estado = detalle?.estadoSolicitud ?? null;

  return {
    idEncabezado,
    resumen: {
      productos: productosAsociados.map((producto) => ({
        idProducto: producto?.id_producto ?? "",
        nombre: producto?.nombre ?? "",
      })),
      estadoActual:
        estado && typeof estado === "object" ? estado?.nombre ?? "" : estado ?? "",
      estadoActualCodigo:
        estado && typeof estado === "object" ? estado?.codigo ?? null : null,
    },
    eventos: items
      .map((item) => normalizeTrazaEvento(item, documentos))
      .sort(sortByFechaDesc),
  };
};